import { useMemo, useState, type FormEvent } from 'react';
import {
  Box,
  Button,
  Field,
  Icon,
  Input,
  NativeSelect,
  SegmentGroup,
  Stack,
  Text,
  useFileUpload,
} from '@chakra-ui/react';
import { LuUpload } from 'react-icons/lu';
import { computeAbono, type AbonoInstallment, type PaydownMode } from 'shared/abono';
import { conceptoDefault } from 'shared/receipt';
import { exportPaymentReceiptPDF } from '@/utils/receipt';
import { registerPayment, uploadReceipt, listContractPayments, type ContractDetail, type PaymentMethod } from '@/lib/api';
import { useProject } from '@/lib/ProjectContext';
import { toaster } from '@/components/ui/toaster';
import { fmtMoney, toMoneyString } from '@/utils/format';
import { RequiredLabel, RequiredLegend } from '@/components/ui/required-field';
import {
  FileUploadHiddenInput,
  FileUploadList,
  FileUploadRootProvider,
  FileUploadTrigger,
} from '@/components/ui/file-upload';

const METHODS: { value: PaymentMethod; label: string }[] = [
  { value: 'cash', label: 'Efectivo' },
  { value: 'transfer', label: 'Transferencia' },
  { value: 'deposit', label: 'Depósito' },
  { value: 'check', label: 'Cheque' },
];

type PaymentType = 'regular' | 'abono_capital';

export function PaymentForm({
  contract,
  onDone,
  onCancel,
}: {
  contract: ContractDetail;
  onDone: () => void;
  onCancel: () => void;
}) {
  const { activeProject } = useProject();
  const nextCuota =
    contract.installments.find((i) => (i.displayStatus ?? i.status) !== 'paid')?.totalAmount ??
    contract.installments[0]?.totalAmount ??
    '';
  // Any rate qualifies (incl. 0%): the recompute is French-shaped and degenerates
  // to an even principal split at 0%. Mirror the server guard in payments.ts.
  const canAbono = contract.saleType === 'financed' && contract.method === 'french';

  const [amount, setAmount] = useState(nextCuota);
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [method, setMethod] = useState<PaymentMethod>('cash');
  const [reference, setReference] = useState('');
  const [notes, setNotes] = useState('');
  const currentCuotaNumber =
    contract.installments.find((i) => (i.displayStatus ?? i.status) !== 'paid')?.installmentNumber ??
    contract.installments[0]?.installmentNumber ??
    1;
  const [payerName, setPayerName] = useState(contract.buyerName);
  const [concept, setConcept] = useState(
    conceptoDefault({ cuotaNumber: currentCuotaNumber, lotNumber: contract.lot?.number, manzana: contract.lot?.manzana }),
  );
  const [paymentType, setPaymentType] = useState<PaymentType>('regular');
  const [paydownMode, setPaydownMode] = useState<PaydownMode>('reduce_cuota');
  const fileUpload = useFileUpload({ maxFiles: 1, accept: ['application/pdf', 'image/jpeg', 'image/png'] });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Live preview — reuse the exact shared orchestrator the server uses. The
  // imputation order is guessed as 'interest_first'; when a real abono happens
  // (E>0) all dues are fully paid, so the recompute is order-independent.
  const preview = useMemo(() => {
    if (paymentType !== 'abono_capital' || !canAbono) return null;
    const money = toMoneyString(amount);
    if (!money || Number(money) <= 0) return null;
    const installments: AbonoInstallment[] = contract.installments.map((i) => ({
      id: i.id,
      installmentNumber: i.installmentNumber,
      dueDate: i.dueDate,
      principalAmount: i.principalAmount,
      interestAmount: i.interestAmount,
      totalAmount: i.totalAmount,
      paidPrincipal: i.paidPrincipal,
      paidInterest: i.paidInterest,
      adminFeeAmount: i.adminFeeAmount,
      paidAdminFee: i.paidAdminFee,
      paidMora: i.paidMora,
    }));
    try {
      return computeAbono({
        installments,
        amount: money,
        paymentDate: date,
        order: 'interest_first',
        annualRate: contract.interestRateAnnual,
        startDate: contract.startDate,
        dayCount: contract.dayCount,
        roundingMode: 'half_even',
        moraMonthlyRate: contract.moraMonthlyRate,
        moraGraceDays: contract.moraGraceDays,
        mode: paydownMode,
      });
    } catch {
      return null;
    }
  }, [paymentType, canAbono, amount, date, paydownMode, contract]);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    const money = toMoneyString(amount);
    if (!money || Number(money) <= 0) {
      setError('Ingresa un monto válido.');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const res = await registerPayment({
        contractId: contract.id,
        paymentDate: date,
        amount: money,
        method,
        reference: reference || undefined,
        notes: notes || undefined,
        payerName: payerName.trim() || undefined,
        concept: concept.trim() || undefined,
        ...(paymentType === 'abono_capital' ? { paymentType, paydownMode } : {}),
      });
      const receipt = fileUpload.acceptedFiles[0];
      if (receipt) await uploadReceipt(res.id, receipt);
      // Auto-open the printable receipt. Best-effort — never block the success flow.
      try {
        const rows = await listContractPayments(contract.id);
        const row = rows.find((r) => r.id === res.id);
        if (row) {
          await exportPaymentReceiptPDF({
            contract,
            payment: row,
            municipalityName: activeProject?.municipalityName ?? null,
          });
        }
      } catch { /* ignore receipt-render errors */ }
      const extra =
        Number(res.principalPaydown ?? '0') > 0
          ? ` · Abonado a capital: ${fmtMoney(res.principalPaydown!)}`
          : Number(res.unapplied) > 0
            ? ` · Excedente: ${fmtMoney(res.unapplied)}`
            : '';
      toaster.create({
        title: 'Pago registrado',
        description: `Recibo ${res.receiptNumber} · Aplicado: ${fmtMoney(res.appliedTotal)}${extra}`,
        type: 'success',
      });
      onDone();
    } catch (err) {
      const raw = err instanceof Error ? err.message : 'No se pudo registrar el pago';
      const msg = raw.includes('abono_needs_reconcile')
        ? 'Regulariza los adelantos de cuota antes de abonar a capital.'
        : raw.includes('abono_not_supported')
          ? 'El abono a capital solo aplica a contratos financiados en francés.'
          : raw;
      setError(msg);
      toaster.create({ title: 'No se pudo registrar el pago', description: msg, type: 'error' });
    } finally {
      setSubmitting(false);
    }
  }

  const ctaLabel = (() => {
    const money = toMoneyString(amount);
    return money && Number(money) > 0 ? `Registrar pago de ${fmtMoney(money)}` : 'Registrar pago';
  })();

  return (
    <Box as="form" onSubmit={handleSubmit} bg="bg.surface" borderWidth="1px" borderColor="border" rounded="lg" p={{ base: '4', md: '5' }}>
      <Stack gap="4">
        <Text fontWeight="700">Registrar pago</Text>

        {canAbono && (
          <SegmentGroup.Root
            value={paymentType}
            onValueChange={(e) => setPaymentType((e.value as PaymentType) ?? 'regular')}
            size="sm"
            // Hug the options instead of stretching to the Stack's full width.
            width="fit-content"
            // Same fix as the sale-type toggle in ContractNew.tsx: the recipe's
            // default --segment-indicator-bg doesn't resolve under our theme, so
            // the selected card would otherwise render with no background.
            css={{ '--segment-indicator-bg': 'colors.bg.surface' }}
          >
            <SegmentGroup.Indicator />
            <SegmentGroup.Item value="regular">
              <SegmentGroup.ItemText>Cuota regular</SegmentGroup.ItemText>
              <SegmentGroup.ItemHiddenInput />
            </SegmentGroup.Item>
            <SegmentGroup.Item value="abono_capital">
              <SegmentGroup.ItemText>Abono a capital</SegmentGroup.ItemText>
              <SegmentGroup.ItemHiddenInput />
            </SegmentGroup.Item>
          </SegmentGroup.Root>
        )}

        <Stack direction={{ base: 'column', md: 'row' }} gap="4">
          <Field.Root flex="1" required>
            <RequiredLabel>Monto (Q)</RequiredLabel>
            <Input
              type="number"
              min="0"
              step="0.01"
              value={amount}
              onChange={(e) => setAmount(e.currentTarget.value)}
              onFocus={(e) => e.currentTarget.select()}
              placeholder="0.00"
            />
          </Field.Root>
          <Field.Root flex="1" required>
            <RequiredLabel>Fecha</RequiredLabel>
            <Input type="date" value={date} onChange={(e) => setDate(e.currentTarget.value)} />
          </Field.Root>
        </Stack>

        {paymentType === 'abono_capital' && (
          <Stack gap="3" bg="bg.subtle" rounded="md" p="3">
            <SegmentGroup.Root
              value={paydownMode}
              onValueChange={(e) => setPaydownMode((e.value as PaydownMode) ?? 'reduce_cuota')}
              size="sm"
              width="fit-content"
              css={{ '--segment-indicator-bg': 'colors.bg.surface' }}
            >
              <SegmentGroup.Indicator />
              <SegmentGroup.Item value="reduce_cuota">
                <SegmentGroup.ItemText>Reducir cuota</SegmentGroup.ItemText>
                <SegmentGroup.ItemHiddenInput />
              </SegmentGroup.Item>
              <SegmentGroup.Item value="reduce_plazo">
                <SegmentGroup.ItemText>Reducir plazo</SegmentGroup.ItemText>
                <SegmentGroup.ItemHiddenInput />
              </SegmentGroup.Item>
            </SegmentGroup.Root>
            {preview && Number(preview.appliedToDues) > 0 && (
              <Text fontSize="sm" color="fg.muted">
                Se cubren primero las cuotas pendientes ({fmtMoney(preview.appliedToDues)}); el resto ({fmtMoney(preview.principalPaydown)}) abona a capital.
              </Text>
            )}
            {preview && Number(preview.principalPaydown) > 0 && (
              <Text fontSize="sm" fontWeight="600">
                {paydownMode === 'reduce_cuota'
                  ? `Tus próximas ${preview.newRemainingCount} cuotas bajan de ${fmtMoney(preview.oldCuota)} a ${fmtMoney(preview.newCuota)}.`
                  : `Tu préstamo termina antes: de ${preview.oldRemainingCount} a ${preview.newRemainingCount} cuotas (misma cuota ${fmtMoney(preview.oldCuota)}).`}
              </Text>
            )}
            {preview && Number(preview.principalPaydown) <= 0 && (
              <Text fontSize="sm" color="orange.600">
                El monto no supera las cuotas pendientes; se registrará como pago regular.
              </Text>
            )}
          </Stack>
        )}

        <Stack direction={{ base: 'column', md: 'row' }} gap="4">
          <Field.Root flex="1">
            <Field.Label>Método</Field.Label>
            <NativeSelect.Root>
              <NativeSelect.Field value={method} onChange={(e) => setMethod(e.currentTarget.value as PaymentMethod)}>
                {METHODS.map((m) => (
                  <option key={m.value} value={m.value}>
                    {m.label}
                  </option>
                ))}
              </NativeSelect.Field>
              <NativeSelect.Indicator />
            </NativeSelect.Root>
          </Field.Root>
          <Field.Root flex="1">
            <Field.Label>Referencia</Field.Label>
            <Input value={reference} onChange={(e) => setReference(e.currentTarget.value)} placeholder="No. boleta / transferencia" />
          </Field.Root>
        </Stack>

        <Stack direction={{ base: 'column', md: 'row' }} gap="4">
          <Field.Root flex="1">
            <Field.Label>Recibí de</Field.Label>
            <Input value={payerName} onChange={(e) => setPayerName(e.currentTarget.value)} onFocus={(e) => e.currentTarget.select()} placeholder="Nombre de quien paga" />
          </Field.Root>
          <Field.Root flex="1">
            <Field.Label>Concepto</Field.Label>
            <Input value={concept} onChange={(e) => setConcept(e.currentTarget.value)} placeholder="Concepto del recibo" />
          </Field.Root>
        </Stack>

        <Field.Root>
          <Field.Label>Notas</Field.Label>
          <Input value={notes} onChange={(e) => setNotes(e.currentTarget.value)} />
        </Field.Root>
        <Field.Root>
          <Field.Label>Comprobante (PDF/JPG/PNG, opcional)</Field.Label>
          <FileUploadRootProvider value={fileUpload}>
            <FileUploadHiddenInput />
            <FileUploadTrigger asChild>
              <Button variant="outline" size="sm" alignSelf="flex-start">
                <Icon as={LuUpload} /> Seleccionar archivo
              </Button>
            </FileUploadTrigger>
            <FileUploadList showSize clearable />
          </FileUploadRootProvider>
        </Field.Root>

        {error && (
          <Text color="red.600" fontSize="sm">
            {error}
          </Text>
        )}
        <RequiredLegend />
        <Stack direction={{ base: 'column-reverse', md: 'row' }} gap="3" justify="flex-end">
          <Button variant="ghost" onClick={onCancel} disabled={submitting}>
            Cancelar
          </Button>
          <Button type="submit" colorPalette="brand" bg="brand.600" color="white" _hover={{ bg: 'brand.700' }} loading={submitting} loadingText="Registrando…">
            {ctaLabel}
          </Button>
        </Stack>
      </Stack>
    </Box>
  );
}
